/* eslint-disable @typescript-eslint/no-unused-vars */
import { Account } from "starknet";

import { IKeybanApi } from "~/api";
import {
  type Address,
  EstimateERC20TransferParams,
  EstimateNftTransferParams,
  type FeeDetails,
  FeesEstimation,
  type Hash,
  type Hex,
  KeybanAccount,
  TransferERC20Params,
  TransferNftParams,
} from "~/index";

/**
 * Starknet-specific {@link KeybanAccount} implementation.
 * Message signing is supported; transfers and token operations are currently unimplemented.
 */
export class StarknetAccount extends KeybanAccount {
  #account: Account;

  address: Address;
  publicKey: Hex;

  constructor(api: IKeybanApi, account: Account, publicKey: Hex) {
    super(api);

    this.#account = account;

    this.address = account.address as Address;
    this.publicKey = publicKey;
  }

  /**
   * Sign a message using Starknet typed-data format.
   * @param message - The message string to include in the typed data.
   * @returns Signature array formatted for Starknet.
   * @example
   * const sig = await account.signMessage("hello");
   * console.log(sig);
   */
  async signMessage(message: string): Promise<string[]> {
    // From string message to typedData message
    const typedMessage = {
      types: {
        StarknetDomain: [
          { name: "name", type: "shortstring" },
          { name: "version", type: "shortstring" },
          { name: "chainId", type: "shortstring" },
          { name: "revision", type: "shortstring" },
        ],
        Message: [{ name: "message", type: "string" }],
      },
      primaryType: "Message",
      domain: {
        name: "StarkNet Message",
        version: "1",
        chainId: "1",
        revision: "1",
      },
      message: { message },
    };
    // Sign the typedData formatted message
    const signedMessage = (await this.#account.signMessage(
      typedMessage,
    )) as string[];
    return signedMessage;
  }

  /**
   * Not implemented.
   * @param _to - Recipient address.
   * @param _value - Amount.
   * @param _fees - Fee details.
   * @throws {Error} Always throws.
   */
  async transfer(
    _to: Address,
    _value: bigint,
    _fees?: FeeDetails,
  ): Promise<Hash> {
    throw new Error("Unimplemented");
  }

  /**
   * Not implemented.
   * @param _to - Recipient address.
   * @throws {Error} Always throws.
   */
  async estimateTransfer(_to: Address): Promise<FeesEstimation> {
    throw new Error("Unimplemented");
  }

  /**
   * Not implemented.
   * @param _params - ERC-20 transfer parameters.
   * @throws {Error} Always throws.
   */
  async transferERC20(_params: TransferERC20Params): Promise<Hash> {
    throw new Error("Unimplemented");
  }

  /**
   * Not implemented.
   * @param _params - ERC-20 estimation parameters.
   * @throws {Error} Always throws.
   */
  async estimateERC20Transfer(
    _params: EstimateERC20TransferParams,
  ): Promise<FeesEstimation> {
    throw new Error("Unimplemented");
  }

  /**
   * Not implemented.
   * @param _params - NFT transfer parameters.
   * @throws {Error} Always throws.
   */
  async transferNft(_params: TransferNftParams): Promise<Hash> {
    throw new Error("Unimplemented");
  }
  /**
   * Not implemented.
   * @param _params - NFT estimation parameters.
   * @throws {Error} Always throws.
   */
  async estimateNftTransfer(
    _params: EstimateNftTransferParams,
  ): Promise<FeesEstimation> {
    throw new Error("Unimplemented");
  }
}
